<script setup lang="ts" generic="T extends Record<string, unknown>">
import { NxDialog } from './../Dialog';
import { NxButton } from './../Button';
import { NxLoadingWrapper, NxSelectInput, NxSwitch } from '../Inputs';
import { computed, inject, ref, watch } from 'vue';
import NxColumnVisibilityList, { type ColumnVisibilityItem } from './NxColumnVisibilityList.vue';
import { i18nInjectionKey } from '../internal';

type ColumnVisibility = { columnId: string; displayName: string; visible: boolean };
const ALLOWED_DELIMITERS = new Map([
  ['Semikolon (;)', ';'],
  ['Komma (,)', ','],
  ['Tabulator (\\t)', '\t'],
  ['Pipe (|)', '|'],
]);

const props = defineProps<{
  initialVisibility: ColumnVisibility[];
  onExportClick: (options: {
    delimiter: string;
    columns: string[];
    fullData: boolean;
  }) => Promise<void>;
}>();
const isDialogOpen = defineModel<boolean>('open', { required: true });

const i18nInjection = inject(i18nInjectionKey)!;

const delimiter = ref<string[]>(['Semikolon (;)']);
const fullData = ref<boolean>(false);
const items = ref<ColumnVisibilityItem[]>(toItems(props.initialVisibility));
// needed as the dialog is only mounted once/not dismounted when closed
watch(
  () => props.initialVisibility,
  (next) => {
    items.value = toItems(next);
  }
);

function toItems(vis: ColumnVisibility[]): ColumnVisibilityItem[] {
  return vis.map(({ columnId, displayName, visible }) => ({
    key: columnId,
    label: displayName,
    visible,
  }));
}

const selectedColumnIds = computed(() =>
  items.value.filter(({ visible }) => visible).map(({ key }) => key)
);

const loading = ref<boolean>(false);
const error = ref<boolean>(false);
</script>

<template>
  <NxDialog
    v-model:open="isDialogOpen"
    :title="i18nInjection.t('common.dialogs.csv_export.title')"
    :description="i18nInjection.t('common.dialogs.csv_export.description')"
    size="default"
    fill
  >
    <template #content>
      <div class="sections">
        <section class="columns-section">
          <h3 class="section-title">{{ i18nInjection.t('common.dialogs.csv_export.columns') }}</h3>
          <NxColumnVisibilityList v-model="items" fill />
        </section>
        <section>
          <h3 class="section-title">
            {{ i18nInjection.t('common.dialogs.csv_export.delimiter') }}
          </h3>
          <NxSelectInput
            v-model="delimiter"
            wrapper-style="width: 100%"
            :options="[...ALLOWED_DELIMITERS.keys()]"
            :placeholder="i18nInjection.t('common.dialogs.csv_export.delimiter_placeholder')"
          />
        </section>
        <section class="section-inline">
          <h3 class="section-title">
            {{ i18nInjection.t('common.dialogs.csv_export.full_data') }}
          </h3>
          <NxSwitch v-model="fullData" height="xs" />
        </section>
      </div>
    </template>
    <template #buttons="{ close }">
      <!-- TODO: find a better way than the loading-wrapper or adapt buttons to it -->
      <NxLoadingWrapper :loading="loading" :error="error" style="width: fit-content">
        <NxButton
          :disabled="delimiter.length === 0 || selectedColumnIds.length === 0"
          @click="
            () => {
              error = false;
              loading = true;
              props
                .onExportClick({
                  delimiter: ALLOWED_DELIMITERS.get(delimiter[0]) ?? ';',
                  columns: selectedColumnIds,
                  fullData,
                })
                .then(() => close())
                .catch(() => (error = true))
                .finally(() => {
                  loading = false;
                });
            }
          "
        >
          {{ i18nInjection.t('common.dialogs.csv_export.export') }}
        </NxButton>
      </NxLoadingWrapper>
      <NxButton variant="outline" @click="close">
        {{ i18nInjection.t('common.dialogs.csv_export.cancel') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.sections {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
  padding-top: var(--nxt-space-sm);
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: auto;
}
.sections > section {
  flex: none;
}
.sections > .columns-section {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.columns-section > .section-title {
  flex: none;
}
.section-title {
  margin: 0 0 var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.section-inline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--nxt-space-md);
}
.section-inline .section-title {
  margin: 0;
}
</style>
